import React from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { Mutation } from '@apollo/client/react/components';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import UpdateVendorInfo from 'four-nets/vendor/update-vendor-info/update-vendor-info';
import { AlertContext } from 'lib/alert-context/alert-context';
import { VendorInformationFragment } from 'four-nets/photoblog/user-blog/author-side/author-side-components/vendor-information/vendor-information.graphql';
import iconPhoto from 'four-nets/icons/svg/icon_photo.svg';
import iconDelete from 'four-nets/icons/svg/icon_delete 2.svg';
import iconSettings from 'four-nets/icons/svg/icon_settings.svg';
import iconEdit from 'four-nets/icons/svg/icon_edit.svg';

import updateCoverImage, {
    updateCoverImageMutation,
    updateCoverImageMutationVariables,
} from '../mutations/update-cover-image.graphql';
import CoverButton from '../cover-button/cover-button';
import defaultStyles from './cover-buttons-owner.scss';

export interface Props {
    styles: typeof defaultStyles;
    username: string;
    hasCoverImage: any;
    buttonsOnly?: boolean;
    vendor: VendorInformationFragment;
}

export interface State {
    key: number;
    loading: boolean;
    open: boolean;
}

class CoverButtonsOwner extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { key: 0, loading: false, open: false };
    }

    render() {
        const { styles, username, hasCoverImage, buttonsOnly, vendor } = this.props;

        return (
            <LoadingWrapperView loading={this.state.loading}>
                <div className={classnames(styles.container, { [styles.mobileContainer]: buttonsOnly })}>
                    {vendor ? (
                        <>
                            <UpdateVendorInfo
                                open={this.state.open}
                                setOpen={(value: boolean = false) => {
                                    this.setState({ open: value });
                                }}
                                vendor={vendor}
                            />
                            <CoverButton
                                tag='button'
                                onClick={() => this.setState({ open: true })}
                                iconSrc={iconEdit}
                                label={<FormattedMessage id='EDIT_VENDOR_INFORMATION' />}
                            />
                        </>
                    ) : null}
                    <CoverButton
                        tag={Link}
                        to={`/settings/${username}/`}
                        icon={
                            <SVGIcon
                                style={{ image: styles.iconWithMargin }}
                                src={iconSettings}
                                className={styles.iconWithMargin}
                            />
                        }
                        label={<FormattedMessage id='BAZAAR_EDIT_PROFILE' />}
                    />
                    <AlertContext.Consumer>
                        {({ acknowledge, confirm }) => (
                            <Mutation<updateCoverImageMutation, updateCoverImageMutationVariables>
                                mutation={updateCoverImage}
                            >
                                {updateCoverImageFn => {
                                    return (
                                        <React.Fragment>
                                            <CoverButton
                                                style={{ wrapper: styles.inputButtonWrapper }}
                                                tag='span'
                                                iconSrc={iconPhoto}
                                                label={
                                                    <FormattedMessage
                                                        id='CHANGE_COVER_PHOTO'
                                                        values={{ gender: 'v' }}
                                                    />
                                                }
                                            >
                                                <input
                                                    type='file'
                                                    key={this.state.key}
                                                    className={styles.input}
                                                    multiple={false}
                                                    accept='image/x-png,image/bmp,image/jpeg'
                                                    onChange={event => {
                                                        if (event.target.files && event.target.files.length) {
                                                            this.setState({ loading: true });
                                                            updateCoverImageFn({
                                                                variables: { file: event.target.files[0] },
                                                            })
                                                                .then(() => {
                                                                    this.setState({ key: this.state.key + 1 });
                                                                })
                                                                .catch((err: Error) => {
                                                                    acknowledge({ body: err.message });
                                                                })
                                                                .finally(() => {
                                                                    this.setState({ loading: false });
                                                                });
                                                        }
                                                    }}
                                                />
                                            </CoverButton>
                                            {!hasCoverImage ? null : (
                                                <CoverButton
                                                    tag='button'
                                                    iconSrc={iconDelete}
                                                    label={
                                                        <FormattedMessage
                                                            id='DELETE_PHOTOBLOG_COVER_PHOTO'
                                                            values={{ gender: 'v' }}
                                                        />
                                                    }
                                                    onClick={() => {
                                                        const onAccept = () => {
                                                            updateCoverImageFn({ variables: { file: null } }).catch(
                                                                (err: Error) => {
                                                                    acknowledge({ body: err.message });
                                                                }
                                                            );
                                                        };
                                                        confirm({
                                                            body: (
                                                                <FormattedMessage id='COVER_IMAGE_DELETE_CONFIRMATION_TEXT' />
                                                            ),
                                                            onAccept,
                                                        });
                                                    }}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                }}
                            </Mutation>
                        )}
                    </AlertContext.Consumer>
                </div>
            </LoadingWrapperView>
        );
    }
}

export default Styled(defaultStyles)(CoverButtonsOwner);
